<template>
  <b-row>

    <b-col cols="12" v-if="!isRecruiter">
      <b-form-group :label="$t('contacts.labels.name')" label-for="name">
        <b-form-input id="name" placeholder="e.g. Eric Raynor" v-model.trim="form.name"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols">
      <b-form-group :label="$t('contacts.labels.companyName')" label-for="company">
        <b-form-input id="company" placeholder="e.g. Google, Facebook" v-model.trim="form.companyName"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols">
      <b-form-group :label="$t('contacts.labels.jobTitle')" label-for="jobTitle">
        <b-form-input id="jobTitle" placeholder="e.g. Designer, Manager" v-model.trim="form.jobTitle"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols">
      <b-form-group>
        <label for="location" class="d-flex flex-row justify-content-between align-items-center">
          {{ $t('contacts.labels.location') }}
          <i18n path="general.search-within" tag="span" style="font-weight: normal;">
            <template v-slot:distance>
              <location-distance-dropdown v-model="form.locationDistance"/>
            </template>
          </i18n>
        </label>
        <location-search id="location" v-model="form.location"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols">
      <b-form-group :label="$t('contacts.labels.industry')" label-for="industry">
        <b-form-input id="industry" placeholder="e.g. Fintech, Proptech" v-model.trim="form.industry"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols">
      <b-form-group :label="$t('contacts.labels.skills')" label-for="skills">
        <b-form-input id="skills" placeholder="e.g. iOS Development, UI &amp; UX" v-model.trim="form.skill"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols">
      <b-form-group :label="$t('contacts.labels.education')" label-for="education">
        <b-form-input id="education" placeholder="e.g. Harvard, Oxford" v-model.trim="form.education"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols">
      <b-form-group :label="$t('jobs.labels.matches')" label-for="matches">
        <toggle id="matches" :label-true="$t('contacts.all-contacts')" :label-false="$t('contacts.matches-only')" v-model="form.ignoreMatches"/>
      </b-form-group>
    </b-col>

  </b-row>
</template>

<script>
import _get from 'lodash/get';
import _set from 'lodash/set';
import Multiselect from 'vue-multiselect';
import Toggle from '@/components/misc/toggle';
import Slider from '@/components/misc/slider';
import LocationSearch from '@/components/misc/LocationSearch';
import LocationDistanceDropdown from '@/components/misc/LocationDistanceDropdown';
import { mapRoot } from '@src/mixins/root';

const INPUT_MAP = {
  'name': 'contact.fullName',
  'companyName': 'contact.employment.companyName',
  'jobTitle': 'contact.employment.title',
  'skill': 'contact.skill',
  'education': 'contact.education.schoolName',
  'location': 'contact.location',
  // 'search': 'contact.search',
  'industry': 'contact.industry',
};

export default {
  props: {
    orientation: {
      type: String,
      default: () => 'horizontal',
      validate: layout => [ 'horizontal', 'vertical' ].includes(layout),
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    Multiselect,
    Toggle,
    Slider,
    LocationSearch,
    LocationDistanceDropdown,
  },
  data() {
    const form = {};

    for (const prop in INPUT_MAP) {
      if (INPUT_MAP.hasOwnProperty(prop)) {
        if (prop === 'location' && _get(this.value, INPUT_MAP[prop], null)) {
          const { id: locationID, latlonWithin } = _get(this.value, INPUT_MAP[prop], {});
          form.location = { id: locationID };
          form.locationDistance = latlonWithin || null;
        } else {
          form[prop] = _get(this.value, INPUT_MAP[prop], null);
        }
      }
    }

    if (this.value.relevance_gte) {
      form.ignoreMatches = false;
    } else {
      form.ignoreMatches = true;
    }

    return { defaults: { ...form }, form };
  },
  computed: {
    ...mapRoot([ 'isRecruiter' ]),
    cols() {
      if (this.orientation === 'horizontal') {
        return { cols: 12, sm: 6, md: 4 };
      } else {
        return { cols: 12, sm: 6, md: 12 };
      }
    },
  },
  watch: {
    form: {
      deep: true,
      handler(value) {
        const { form } = this;
        const where = {};

        for (const prop in INPUT_MAP) {
          if (INPUT_MAP.hasOwnProperty(prop)) {
            if (form[prop]) {
              if (prop === 'location') {
                const { id: locationID } = form[prop] || {};
                if (locationID) {
                  _set(where, INPUT_MAP[prop], { id: locationID });

                  if (form.locationDistance) {
                    _set(where, `${INPUT_MAP[prop]}.latlonWithin`, form.locationDistance);
                  }
                }
              } else if (INPUT_MAP[prop]) {
                _set(where, INPUT_MAP[prop], form[prop]);
              }
            }
          }
        }

        if (form.ignoreMatches === false) {
          _set(where, 'relevance_gte', 30.0);
        }

        _set(where, 'unsuitable', false);
        this.$emit('input', where);
      },
    },
    'form.location'() {
      const { defaults, form } = this;
      if (defaults.ignoreMatches === form.ignoreMatches) {
        // Flick the ignoreMatches value if still default & location is set
        form.ignoreMatches = true;
        defaults.ignoreMatches = null;
      }
    },
  },
};
</script>

<style lang="scss">
.contacts-filter-horizontal-options {

  .col {
    margin: 5px 0 5px 0;
  }

  .left-column {
    padding-left: 0.75em;
    padding-right: 0.75em;
  }
  .middle-column {
    padding-left: 0.75em;
    padding-right: 0.75em;

  }
  .right-column {
    padding-left: 0.75em;
    padding-right: 0.75em;
  }
  .form-group {
    margin-bottom: 1.7em;
  }

}
</style>



// WEBPACK FOOTER //
// src/components/contacts-filter/horizontal-options.vue